import { memo, useCallback, useMemo, useState } from 'react';
import { DateRange } from 'react-aria-components';

import {
  Button,
  DateRangePicker,
  Flex,
  Heading,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  SelectValue,
  Text,
} from '@/components/core';
import { ChevronIcon, DateIcon, InsightsIcon } from '@/components/icons';

import { FieldBreakdown } from './FieldBreakdown';
import { FieldGraph } from './FieldGraph';
import { GlobalGraph } from './GlobalGraph';
import { MetricCard } from './MetricCard';
import { useStatistics } from './useStatistics';
import { createDateRange, createMetric, DEFAULT_NUM_DAYS, getData, MetricType } from './utils';

export type InsightsProps = object;

// eslint-disable-next-line no-empty-pattern
const IInsights = ({}: InsightsProps) => {
  const [dateSelection, setDateSelection] = useState<string | number>(DEFAULT_NUM_DAYS);
  const [dateRange, setDateRange] = useState<DateRange | null>(createDateRange(DEFAULT_NUM_DAYS));
  const [selectedMetric, setSelectedMetric] = useState<MetricType>('runs');

  const setDateRangeOption = useCallback((days: number) => {
    setDateSelection(days);
    setDateRange(createDateRange(days));
  }, []);

  const setCustomDateRangeOption = useCallback((range: DateRange | null) => {
    if (range) {
      setDateSelection('custom');
      setDateRange(range);
    }
  }, []);

  const { statistics, previousStatistics } = useStatistics({ dateRange, dateSelection });

  const { runs, automation, fullAutomation, flagged, edits } = useMemo(() => {
    const data = getData(statistics?.statistics.global);
    const previousData = getData(previousStatistics?.statistics.global);

    const runs = {
      value: data.numDocuments,
      change: data.numDocuments - previousData.numDocuments,
      datapoints: data.numValues,
    };

    return {
      runs,
      automation: createMetric(
        data.numValuesAutomated,
        data.numValues,
        previousData.numValuesAutomated,
        previousData.numValues
      ),
      fullAutomation: createMetric(
        data.numDocumentsAutomated,
        data.numDocuments,
        previousData.numDocumentsAutomated,
        previousData.numDocuments
      ),
      flagged: createMetric(
        data.numValuesFlagged,
        data.numValues,
        previousData.numValuesFlagged,
        previousData.numValues
      ),
      edits: createMetric(data.numValuesEdited, data.numValues, previousData.numValuesEdited, previousData.numValues),
    };
  }, [statistics, previousStatistics]);

  const isEmpty = useMemo(() => runs.datapoints === 0, [runs]);

  return (
    <div className="flex min-h-0 w-full flex-col gap-4 overflow-y-auto bg-gray-50">
      <div className="py-md flex h-12 w-full items-center gap-2 border-b-1 border-gray-200 bg-white px-12">
        <div className="gap-sm flex items-center">
          <Heading className="font-medium text-gray-500" size="sm">
            Insights
          </Heading>
          <Text className="font-bold text-gray-200">/</Text>
          <Heading className="font-medium text-gray-600" size="sm">
            Overview
          </Heading>
        </div>
      </div>

      <div className="mx-72 my-6 flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <Heading size="xl" className="text-gray-900">
            Insights
          </Heading>

          <div className="grow" />

          <div className="gap-sm flex items-center">
            <Select
              value={typeof dateSelection === 'number' ? dateSelection : null}
              placeholder="Custom"
              onChange={(days) => {
                setDateRangeOption(Number(days?.toString() ?? DEFAULT_NUM_DAYS));
              }}
            >
              <div>
                <Button variant="secondary">
                  <SelectValue />
                  <ChevronIcon rotation="down" />
                </Button>
              </div>

              <Popover>
                <ListBox className="max-h-64 overflow-y-auto">
                  {[1, 7, 14, 30, 90].map((days) => (
                    <ListBoxItem key={days} id={days}>
                      {days === 1 ? <Text>Last 24 hours</Text> : <Text>{`Last ${days} days`}</Text>}
                    </ListBoxItem>
                  ))}
                </ListBox>
              </Popover>
            </Select>

            <div className="flex items-center rounded-lg border border-gray-200 bg-white pl-2 shadow-xs hover:bg-gray-100">
              <DateIcon />
              <DateRangePicker
                label="Select date range"
                dateRange={dateRange}
                onDateRangeChange={setCustomDateRangeOption}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-5 rounded-lg border border-gray-200 bg-white">
          <MetricCard
            className="col-span-1 border-r border-r-gray-200"
            heading="Agent runs"
            tooltip="The number of times the agent has been executed, including both successful and failed runs."
            isSelected={selectedMetric === 'runs'}
            isEmpty={isEmpty}
            onClick={() => setSelectedMetric('runs')}
            datapointsLabel="values"
            {...runs}
          />

          <MetricCard
            className="col-span-1 border-r border-r-gray-200"
            heading="Automated"
            tooltip="The amount of values automatically validated without requiring human review."
            isPercentage
            isSelected={selectedMetric === 'automation'}
            isEmpty={isEmpty}
            onClick={() => setSelectedMetric('automation')}
            datapointsLabel="values"
            {...automation}
          />

          <MetricCard
            className="col-span-1 border-r border-r-gray-200"
            heading="Reviewed"
            tooltip="The amount of values flagged for human review because the agent's confidence was below the set threshold or validation checks failed."
            isPercentage
            lowerIsBetter
            isSelected={selectedMetric === 'flagged'}
            isEmpty={isEmpty}
            onClick={() => setSelectedMetric('flagged')}
            datapointsLabel="values"
            {...flagged}
          />
          <MetricCard
            className="col-span-1 border-r border-r-gray-200"
            heading="Corrected"
            tooltip="The amount of values where a human made changes during review, indicating the agent's output needed correction."
            isPercentage
            lowerIsBetter
            isSelected={selectedMetric === 'edits'}
            isEmpty={isEmpty}
            onClick={() => setSelectedMetric('edits')}
            datapointsLabel="values"
            {...edits}
          />

          <MetricCard
            className="col-span-1"
            heading="Fully automated"
            tooltip="The amount of documents automatically validated without requiring human review."
            isPercentage
            isSelected={selectedMetric === 'fullAutomation'}
            isEmpty={isEmpty}
            onClick={() => setSelectedMetric('fullAutomation')}
            datapointsLabel="runs"
            {...fullAutomation}
          />

          <div className="col-span-5 m-4">
            {isEmpty ? (
              <div className="gap-md flex h-[40vh] flex-col items-center justify-center">
                <div className="flex items-center justify-center rounded-lg bg-gray-100 p-2">
                  <InsightsIcon />
                </div>
                <div className="gap-sm flex flex-col items-center justify-center">
                  <Heading>No usage data</Heading>
                  <Text className="text-gray-400">The selected data range doesn't have any usage data</Text>
                </div>
              </div>
            ) : (
              <GlobalGraph dateRange={dateRange} dateSelection={dateSelection} selectedMetric={selectedMetric} />
            )}
          </div>
        </div>

        <div className="grid h-[35vh] grid-cols-5 gap-4">
          <Flex col variant="secondary" className="col-span-3 rounded-lg shadow-xs">
            <div className="overflow-auto">
              <FieldBreakdown dateRange={dateRange} dateSelection={dateSelection} isEmpty={isEmpty} />
            </div>
          </Flex>

          <FieldGraph dateRange={dateRange} dateSelection={dateSelection} isEmpty={isEmpty} className="col-span-2" />
        </div>
      </div>
    </div>
  );
};

export const Insights = memo(IInsights) as typeof IInsights;
